import { Building07, Image01, LogOut01, Users01 } from "@untitledui/icons";
import { Outlet, useLoaderData, useLocation, useNavigate, useNavigation } from "react-router";
import { RouteSkeleton, Skeleton } from "@/components/admin/loading";
import { MobileNavigationHeader } from "@/components/application/app-navigation/base-components/mobile-header";
import { NavList } from "@/components/application/app-navigation/base-components/nav-list";
import type { NavItemType } from "@/components/application/app-navigation/config";
import { ToastProvider } from "@/components/admin/toast";
import { ButtonUtility } from "@/components/base/buttons/button-utility";
import { RouteProvider } from "@/providers/router-provider";
import type { AdminMe, Api } from "../lib/api.js";

const NAV: NavItemType[] = [
  { label: "用户", href: "/users", icon: Users01 },
  { label: "工作区", href: "/workspaces", icon: Building07 },
  { label: "资源", href: "/assets", icon: Image01 },
];

const Brand = () => <span className="text-md font-semibold text-primary">Diotia Admin</span>;

/** Everything sits inside this: React Aria's links navigate through the router, and any page may raise a toast. */
export function Root() {
  return (
    <RouteProvider>
      <ToastProvider>
        <Outlet />
      </ToastProvider>
    </RouteProvider>
  );
}

/**
 * Also the shell route's HydrateFallback: on a fresh load it renders before the gate has read the session,
 * with no loader data, around the page's skeleton. Being the same component both times, it stays mounted
 * (navigation included) when the data arrives.
 */
export function Shell() {
  const data = useLoaderData<{ api: Api; me: AdminMe }>() as { api: Api; me: AdminMe } | undefined;
  const { pathname } = useLocation();
  const navigate = useNavigate();
  // The highlight follows the click, not the page load.
  const going = useNavigation().location?.pathname ?? pathname;
  const active = NAV.find((item) => item.href && going.startsWith(item.href))?.href;

  const sidebar = (
    <aside className="flex h-full w-full flex-col justify-between overflow-auto border-secondary bg-primary pt-5 lg:w-70 lg:border-r">
      <div className="px-5">
        <Brand />
      </div>
      {/* Keyed: Untitled UI's NavList reads the active item only when it mounts. */}
      <NavList key={active} activeUrl={active} items={NAV} />
      <div className="mt-auto flex items-center justify-between gap-3 border-t border-secondary px-5 py-4">
        {data ? (
          <>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-primary">{data.me.name}</p>
              <p className="truncate text-sm text-tertiary">{data.me.email}</p>
            </div>
            <ButtonUtility size="sm" color="tertiary" tooltip="退出登录" icon={LogOut01} onClick={async () => (await data.api.signOut(), void (await navigate("/sign-in")))} />
          </>
        ) : (
          <div className="flex min-w-0 flex-1 flex-col gap-1.5 py-0.5">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-36" />
          </div>
        )}
      </div>
    </aside>
  );

  return (
    <div className="flex min-h-dvh flex-col bg-secondary_subtle lg:flex-row">
      <MobileNavigationHeader brand={<Brand />}>{sidebar}</MobileNavigationHeader>
      <div className="hidden lg:fixed lg:inset-y-0 lg:left-0 lg:flex">{sidebar}</div>
      <main className="min-w-0 flex-1 lg:pl-70">
        <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-4 py-8 lg:px-8">
          {/* Keyed by path, so another record of the same kind (one person → another) is a new page that shows
              its skeleton at once, rather than the old one held until the new one loads. */}
          {data ? <Outlet key={pathname} /> : <RouteSkeleton />}
        </div>
      </main>
    </div>
  );
}

/** Page title on the left, its actions on the right. */
export function PageHeader({ title, description, badge, actions }: { title: React.ReactNode; description?: React.ReactNode; badge?: React.ReactNode; actions?: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-display-xs font-semibold text-primary">{title}</h1>
          {badge}
        </div>
        {description && <p className="mt-1 text-sm text-tertiary">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-3">{actions}</div>}
    </div>
  );
}

export const Card = ({ title, actions, children }: { title?: React.ReactNode; actions?: React.ReactNode; children: React.ReactNode }) => (
  <section className="rounded-xl bg-primary shadow-xs ring-1 ring-secondary">
    {(title || actions) && (
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-secondary px-6 py-4">
        {title && <h2 className="text-md font-semibold text-primary">{title}</h2>}
        {actions}
      </header>
    )}
    {children}
  </section>
);
